import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import LoadingIndicator from 'components/LoadingIndicator';
import QuestListItem from 'components/QuestListItem';
import './QuestList.css';

const WSTORE_LINK_TO = {
  pathname: '/wstore',
  state: {
    categoryValue: 'Quest',
    initiator: 'quests-wstore-button'
  }
};

function QuestList({ questStatuses, loading, error, active }) {
  let content = null;

  if (loading) {
    content = <LoadingIndicator expand />;
  } else if (error) {
    content = (
      <div className="alert alert-danger">
        There was a problem retrieving your list of quests. Please try again
        later.
      </div>
    );
  } else if (questStatuses.length === 0) {
    content = (
      <div className="empty-quests-message">
        <span className="quest-message-header">
          {active ? 'No Active Quests' : 'No Past Quests'}
        </span>
        <span className="quest-message-text">
          {active
            ? "You don't have any Quests at the moment. Visit the WStore to find new Quests and start your adventure!"
            : "You haven't completed any Quests yet. Visit the WStore to embark on your first adventure!"}
        </span>
        <Link className="quests-wstore-button" to={WSTORE_LINK_TO}>
          Shop Quests
        </Link>
      </div>
    );
  } else {
    if (active) {
      content = (
        <div className="current-quests-container">
          {questStatuses.map((questStatusItem, index) => (
            <QuestListItem
              key={index}
              startDateTime={questStatusItem.preferredPropValidFrom}
              endDateTime={questStatusItem.preferredPropValidTo}
              endDateUtc={questStatusItem.UTCValidTo}
              quest={questStatusItem.quest}
              missionStatuses={questStatusItem.missionStatuses}
            />
          ))}
        </div>
      );
    } else {
      const now = moment();
      const completedQuests = questStatuses
        .filter(questStatus => questStatus.completionDate)
        .sort((questStatus1, questStatus2) => {
          const questStatusMoment1 = moment.utc(
            questStatus1.UTCCompletionDate,
            'YYYY-MM-DD HH:mm:ss.S'
          );
          const questStatusMoment2 = moment.utc(
            questStatus2.UTCCompletionDate,
            'YYYY-MM-DD HH:mm:ss.S'
          );
          if (questStatusMoment1.isSame(questStatusMoment2)) {
            return 0;
          } else if (questStatusMoment1.isBefore(questStatusMoment2)) {
            return 1;
          } else {
            return -1;
          }
        });
      const expiredQuests = questStatuses.filter(
        questStatus =>
          !questStatus.completionDate &&
          !questStatus.abortionDate &&
          moment
            .utc(questStatus.UTCValidTo, 'YYYY/MM/DD HH:mm:ss')
            .isBefore(now)
      );
      const abortedQuests = questStatuses
        .filter(
          questStatus =>
            questStatus.utcAbortionDate && !questStatus.UTCCompletionDate
        )
        .sort((questStatus1, questStatus2) => {
          const questStatusMoment1 = moment.utc(
            questStatus1.utcAbortionDate,
            'YYYY-MM-DD HH:mm:ss.S'
          );
          const questStatusMoment2 = moment.utc(
            questStatus2.utcAbortionDate,
            'YYYY-MM-DD HH:mm:ss.S'
          );
          if (questStatusMoment1.isSame(questStatusMoment2)) {
            return 0;
          } else if (questStatusMoment1.isBefore(questStatusMoment2)) {
            return 1;
          } else {
            return -1;
          }
        });

      content = (
        <React.Fragment>
          {completedQuests.length > 0 ? (
            <div className="completed-quests-container">
              {completedQuests.map((questStatusItem, index) => (
                <QuestListItem
                  key={index}
                  startDateTime={questStatusItem.preferredPropValidFrom}
                  endDateTime={questStatusItem.preferredPropValidTo}
                  endDateUtc={questStatusItem.UTCValidTo}
                  completionDate={questStatusItem.completionDate}
                  quest={questStatusItem.quest}
                  missionStatuses={questStatusItem.missionStatuses}
                  past={true}
                />
              ))}
            </div>
          ) : null}
          {expiredQuests.length > 0 ? (
            <div className="expired-quests-container">
              {expiredQuests.map((questStatusItem, index) => (
                <QuestListItem
                  key={index}
                  startDateTime={questStatusItem.preferredPropValidFrom}
                  endDateTime={questStatusItem.preferredPropValidTo}
                  endDateUtc={questStatusItem.UTCValidTo}
                  quest={questStatusItem.quest}
                  missionStatuses={questStatusItem.missionStatuses}
                  past={true}
                />
              ))}
            </div>
          ) : null}
          {abortedQuests.length > 0 ? (
            <div className="aborted-quests-container">
              {abortedQuests.map((questStatusItem, index) => (
                <QuestListItem
                  key={index}
                  startDateTime={questStatusItem.preferredPropValidFrom}
                  endDateTime={questStatusItem.preferredPropValidTo}
                  endDateUtc={questStatusItem.UTCValidTo}
                  abortionDate={questStatusItem.abortionDate}
                  quest={questStatusItem.quest}
                  missionStatuses={questStatusItem.missionStatuses}
                />
              ))}
            </div>
          ) : null}
        </React.Fragment>
      );
    }
  }

  return <div className="player-quest-list-view">{content}</div>;
}

QuestList.propTypes = {
  questStatuses: PropTypes.arrayOf(
    PropTypes.shape({
      transactionId: PropTypes.number,
      patronAccountNumber: PropTypes.string,
      preferredPropValidFrom: PropTypes.string,
      preferredPropValidTo: PropTypes.string,
      UTCValidFrom: PropTypes.string,
      UTCValidTo: PropTypes.string,
      isComplete: PropTypes.number,
      quest: PropTypes.shape({
        id: PropTypes.string,
        prizeDetails: PropTypes.shape({
          name: PropTypes.string,
          url: PropTypes.string,
          description: PropTypes.string
        }),
        prizes: PropTypes.shape({
          prizes: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.number,
              name: PropTypes.string,
              category: PropTypes.string
            })
          )
        })
      }),
      missionStatuses: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          goal: PropTypes.number,
          progress: PropTypes.number,
          progressText: PropTypes.string,
          mission: PropTypes.shape({
            description: PropTypes.string,
            icons: PropTypes.arrayOf(
              PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string
              })
            )
          })
        })
      )
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.instanceOf(Error)
};

export default QuestList;
